// Durable breadcrumb for the Claude / MCP connector consent flow.
//
// A signed-out user who lands on the consent screen (/connect-ai) is sent to
// the real /sign-up (or /login) page so account creation captures the same data
// as a normal web signup (first/last name, completeSignUp, Mixpanel alias).
// The OAuth params from /connect-ai must survive that hop and the
// SignUp<->Login footer hop, so we park them in localStorage — exactly the
// pendingClaimToken / pendingInviteToken precedent (see Claim.js / AcceptInvite.js).
//
// Precedence on every auth-complete path is:
//   pendingClaimToken > pendingConnectAiParams > pendingRedirectPath
//     > pendingInviteToken > default
//
// Unlike pendingRedirectPath, this breadcrumb is honoured for BRAND-NEW users
// too (a Claude signup is the whole point) — that's why it's a dedicated key
// rather than reusing pendingRedirect, whose new-user semantics discard it.

const PENDING_CONNECT_AI_KEY = 'pendingConnectAiParams';

// How long a parked request stays valid. Generous enough to finish a signup
// (including the email-already-exists → Login footer hop), short enough that an
// abandoned flow can't hijack an unrelated signup the user starts days later.
const TTL_MS = 30 * 60 * 1000;

// Mirror of the connector's server-side redirect_uri allowlist (functions/
// clozersConnector/oauth.js). The Authorize step always re-validates server-side,
// but we also gate the parked value here so a crafted
// /connect-ai?redirect_uri=… link can never be replayed into an off-allowlist
// redirect (e.g. via the Cancel button) after the auth hop.
const REDIRECT_ALLOWLIST_EXACT = [
    'https://claude.ai/api/mcp/auth_callback',
    'https://claude.com/api/mcp/auth_callback',
    'https://chatgpt.com/connector_platform_oauth_redirect',
    'https://chatgpt.com/oauth/callback',
    'https://chat.openai.com/oauth/callback',
];

// ChatGPT's current connector flow mints a unique per-connector production
// callback, https://chatgpt.com/connector/oauth/<opaque-id>, so match it
// structurally. Mirrors isChatgptConnectorCallback in the server (functions/
// clozersConnector/oauth.js): require https, an EXACT parsed hostname (not a
// prefix/suffix — 'chatgpt.com.evil.com' and 'evil.chatgpt.com' both fail), no
// userinfo, and a non-empty id segment under the fixed /connector/oauth/ path.
const CHATGPT_CONNECTOR_CALLBACK_PREFIX = '/connector/oauth/';

export const isAllowedRedirect = (uri) => {
    if (typeof uri !== 'string' || !uri) return false;
    if (REDIRECT_ALLOWLIST_EXACT.includes(uri)) return true;
    let parsed;
    try {
        parsed = new URL(uri);
    } catch {
        return false;
    }
    if (
        parsed.protocol === 'https:' &&
        parsed.hostname === 'chatgpt.com' &&
        parsed.username === '' &&
        parsed.password === '' &&
        parsed.pathname.startsWith(CHATGPT_CONNECTOR_CALLBACK_PREFIX) &&
        parsed.pathname.length > CHATGPT_CONNECTOR_CALLBACK_PREFIX.length
    ) {
        return true;
    }
    // Loopback dev clients only (MCP Inspector / mcp-remote pick a random port):
    // plain http, a real loopback hostname, and NO userinfo. The userinfo guard
    // matters — a prefix check like uri.startsWith('http://localhost:') would
    // accept 'http://localhost:8080@evil.com', which actually resolves to
    // evil.com. Matches the parsed-hostname check the server uses.
    return (
        parsed.protocol === 'http:' &&
        (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') &&
        parsed.username === '' &&
        parsed.password === ''
    );
};

// A parked value is only valid if it carries a client_id and an allowlisted
// redirect_uri — the same two fields ConnectAI requires for paramsValid.
const isValidParams = (searchString) => {
    if (typeof searchString !== 'string' || searchString === '') return false;
    try {
        const params = new URLSearchParams(searchString);
        return Boolean(params.get('client_id')) && isAllowedRedirect(params.get('redirect_uri'));
    } catch {
        return false;
    }
};

// Store the raw OAuth query string (searchParams.toString(), no leading '?')
// alongside a timestamp for TTL enforcement.
export const setPendingConnectAi = (searchString) => {
    if (typeof window === 'undefined') return;
    if (!isValidParams(searchString)) return; // silently refuse — no-op
    try {
        window.localStorage.setItem(
            PENDING_CONNECT_AI_KEY,
            JSON.stringify({ p: searchString, t: Date.now() })
        );
    } catch { /* ignore */ }
};

// Read + re-validate WITHOUT removing — used by the auth pages to decide whether
// this completion should return to the consent screen before they navigate.
// Returns the raw query string, or null if missing / malformed / expired /
// no-longer-allowlisted.
export const peekPendingConnectAi = () => {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(PENDING_CONNECT_AI_KEY);
        if (!raw) return null;
        let parsed;
        try { parsed = JSON.parse(raw); } catch { return null; }
        if (!parsed || typeof parsed.p !== 'string') return null;
        if (!Number.isFinite(parsed.t) || Date.now() - parsed.t > TTL_MS) return null;
        return isValidParams(parsed.p) ? parsed.p : null;
    } catch { return null; }
};

// Discard the breadcrumb (call once the return navigation has been issued).
export const clearPendingConnectAi = () => {
    if (typeof window === 'undefined') return;
    try { window.localStorage.removeItem(PENDING_CONNECT_AI_KEY); }
    catch { /* ignore */ }
};
